import { supabase } from '../../lib/supabase';
import type { NivelDoConvite, ArtistMember, AccessLevel } from '../../interfaces/maestra';
import { ambiente } from '../../nucleo/ambiente';
import { PRIVILEGIO_DO_CONVIDADO } from '../../constants/maestra';

const TABLE = 'artist_members';

export interface TitularDoPerfil { nome: string; foto: string | null }

/**
 * O nome e a foto do TITULAR do perfil, para a equipe (RPC `titular_do_perfil`). O nome mora nos
 * metadados do login, que o cliente não lê de outra conta. Quem não é dono nem membro ativo
 * recebe `null`.
 */
export const titularDoPerfil = async (artistId: string): Promise<TitularDoPerfil | null> => {
  const { data, error } = await supabase.rpc('titular_do_perfil', { p_artist_id: artistId });
  if (error) throw error;
  const linha = Array.isArray(data) ? data[0] : data;
  return linha?.nome ? { nome: linha.nome, foto: linha.foto ?? null } : null;
};

export const listMembers = async (artistId: string): Promise<ArtistMember[]> => {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('artist_id', artistId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []) as ArtistMember[];
};

/**
 * O convite grava o acesso por módulo que o titular escolheu (`niveis`, de `niveisDoConvite`):
 * Editar em tudo é `full`. Sem `niveis`, `full`, como sempre foi. Editar, ainda assim, exige o
 * perfil do titular ativo: a regra vive em `useArtistCapabilities` e na RLS.
 */
export const inviteMember = async (input: {
  artistId: string;
  email: string;
  name?: string;
  /** O que a pessoa faz e o telefone (o músico convidado pela Formação ou pelo show). */
  funcao?: string | null;
  telefone?: string | null;
  /** O acesso por módulo (`niveisDoConvite`). Sem ele, Editar em tudo. */
  niveis?: NivelDoConvite[];
  /** @deprecated Use `niveis`. Ignorado. */
  accessLevels?: AccessLevel[];
}): Promise<ArtistMember & { emailEnviado: Promise<boolean> }> => {
  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      artist_id: input.artistId,
      // Minúsculo e sem espaço: o convidado entra casando o e-mail do login com este, e
      // "Bia@x.com " nunca casaria (QA da Equipe, 26/09).
      email: input.email.trim().toLowerCase(),
      name: input.name || null,
      // Só com valor: sem a migração 20261101130000, a coluna não existe e o convite não pode cair.
      ...(input.funcao?.trim() ? { funcao: input.funcao.trim() } : {}),
      ...(input.telefone?.trim() ? { telefone: input.telefone.trim() } : {}),
      access_levels: input.niveis?.length ? input.niveis : PRIVILEGIO_DO_CONVIDADO,
      status: 'pending',
    })
    .select('*')
    .single();
  if (error) throw error;
  const member = data as ArtistMember;
  // O e-mail sai em segundo plano: a linha 'pending' já existe e aparece para o convidado ao
  // entrar com este e-mail. Mas a falha NÃO some mais: `invoke` não rejeita em 4xx/5xx, ele
  // devolve `{ error }`, e o `.catch` de antes nunca pegava a Brevo fora do ar. Quem quiser saber
  // espera `member.emailEnviado`.
  const emailEnviado = supabase.functions
    .invoke('send-team-invite', { body: { memberId: member.id, appUrl: ambiente().origemDoApp } })
    .then(({ error: e }) => {
      if (e) console.error('send-team-invite falhou:', e.message || e);
      return !e;
    })
    .catch((e) => { console.error('send-team-invite falhou:', e?.message || e); return false; });
  return Object.assign(member, { emailEnviado });
};

/**
 * Convidar de novo quem RECUSOU: a mesma linha volta a pendente (um convite por e-mail e por
 * perfil, índice `artist_members_um_por_email`) e o e-mail sai de novo.
 */
export const convidarDeNovo = async (artistId: string, memberId: string, niveis?: NivelDoConvite[]): Promise<ArtistMember> => {
  const { data, error } = await supabase
    .from(TABLE)
    // O convite de novo leva o acesso escolhido agora (sem ele, fica o que a linha já tinha).
    .update({ status: 'pending', user_id: null, ...(niveis ? { access_levels: niveis } : {}) })
    .eq('id', memberId)
    .eq('artist_id', artistId)
    .select('*')
    .single();
  if (error) throw error;
  await reenviarConvite(memberId);
  return data as ArtistMember;
};

// `access_levels` não entra neste patch: o acesso por módulo tem a função dele (`mudarAcessoDoMembro`).
// `status` saiu também (QA da Equipe, 26/09):
// marcar um pendente como ativo, sem `user_id`, tirava o convite da lista do convidado e não
// dava acesso a ninguém. Entrar é o convidado aceitar; sair é remover.
//
// `artistId` filtra junto: quem é titular de dois perfis não mexe num membro do outro por uma
// tela velha.
export const updateMember = async (
  id: string,
  patch: { name?: string; funcao?: string | null; telefone?: string | null },
  artistId?: string
): Promise<ArtistMember> => {
  // Só o que veio: um campo ausente não é apagado.
  const campos = Object.fromEntries(Object.entries({ name: patch.name, funcao: patch.funcao, telefone: patch.telefone }).filter(([, v]) => v !== undefined));
  let q = supabase.from(TABLE).update(campos).eq('id', id);
  if (artistId) q = q.eq('artist_id', artistId);
  // `.single()` com zero linhas é erro: a RLS negando deixa de virar "salvo".
  const { data, error } = await q.select('*').single();
  if (error) throw error;
  return data as ArtistMember;
};

/**
 * O titular muda o acesso por módulo de quem já está na equipe (`niveisDoConvite`). Só o titular
 * consegue: a RLS de `artist_members` deixa escrever só o dono do perfil.
 */
export const mudarAcessoDoMembro = async (id: string, artistId: string, niveis: NivelDoConvite[]): Promise<ArtistMember> => {
  const { data, error } = await supabase.from(TABLE)
    .update({ access_levels: niveis })
    .eq('id', id).eq('artist_id', artistId)
    .select('*').single();
  if (error) throw error;
  return data as ArtistMember;
};

export const removeMember = async (id: string, artistId?: string): Promise<void> => {
  let q = supabase.from(TABLE).delete().eq('id', id);
  if (artistId) q = q.eq('artist_id', artistId);
  const { data, error } = await q.select('id');
  if (error) throw error;
  // Delete negado pela RLS não é erro, é zero linhas. Sem esta conta, a tela dizia "removido" e a
  // pessoa voltava no próximo tempo real.
  if (!data?.length) throw new Error('Não deu para remover.');
};

// ---- Convites pendentes (lado do convidado) ------------------------------------------------

// Convite pendente já com nome/imagem/gênero do artista (via RPC SECURITY DEFINER, pois o
// convidado ainda NÃO é membro e a RLS bloqueia o join direto na tabela artists).
export interface PendingInvite {
  id: string;
  artist_id: string;
  access_levels: AccessLevel[];
  status: string;
  created_at: string;
  artist_name: string | null;
  artist_image: string | null;
  artist_genre: string | null;
}

// A RPC filtra pelo e-mail do próprio usuário logado (auth.jwt) — nenhum parâmetro necessário.
export const fetchPendingInvites = async (): Promise<PendingInvite[]> => {
  const { data, error } = await supabase.rpc('get_pending_invites');
  if (error) throw error;
  return (data || []) as PendingInvite[];
};

// Aceitar e recusar conferem a linha que voltou: com a RLS negando, o update atualizava zero
// linhas SEM erro, e a tela dava as boas-vindas a quem continuava de fora. O nome que o titular
// deu fica (o gatilho `fn_convite_imutavel` garante); o da conta só entra se não havia nenhum.
export const acceptInvite = async (inviteId: string, userId: string, userName?: string): Promise<void> => {
  const payload: Record<string, unknown> = { status: 'active', user_id: userId };
  if (userName) payload.name = userName;
  const { data, error } = await supabase.from(TABLE).update(payload).eq('id', inviteId).select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('Este convite não está mais disponível.');
};

export const rejectInvite = async (inviteId: string): Promise<void> => {
  const { data, error } = await supabase.from(TABLE).update({ status: 'rejected' }).eq('id', inviteId).select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('Este convite não está mais disponível.');
};

/**
 * Reenvia o e-mail do convite (QA da Equipe, 26/09): o convite pendente não tinha como ser
 * mandado de novo. Diferente do envio do convite, aqui o erro volta para quem clicou.
 */
export const reenviarConvite = async (memberId: string): Promise<void> => {
  const { error } = await supabase.functions.invoke('send-team-invite', { body: { memberId, appUrl: ambiente().origemDoApp } });
  if (error) {
    // 429 é o freio da edge (um e-mail por convite a cada poucos minutos): a tela diz "espere".
    const status = (error as { context?: { status?: number } }).context?.status;
    throw Object.assign(new Error(error.message), { code: status === 429 ? 'espere' : 'falhou' });
  }
};

/** O e-mail como o banco o compara: sem espaço e sem diferença de maiúscula. */
export const emailDoConvite = (email: string): string => email.trim().toLowerCase();
